// 库存 → 取走人管理 (manage non-staff 店员取走 names). 店长以上.
//
// The custom-taker list is the canonical home for non-staff people who took
// stock. This page lets a manager tidy it WITHOUT touching the append-only
// inventory ledger:
//   * 重命名 — fixes a typo; historical rows display the new name (JOIN follows)
//   * 停用 / 恢复 — drops/restores a name in the 报废 dropdown (history preserved)
//   * 合并 — alias-chain: folds a duplicate into another; events are never rewritten

import { useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api.ts';
import { Toast } from '../components/atoms.jsx';
import { Modal } from '../components/Modal.jsx';

function fmtWhen(s) {
  return typeof s === 'string' ? s.replace('T', ' ').slice(0, 16) : '';
}

export const AdminCustomTakers = () => {
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [mergeFrom, setMergeFrom] = useState(null);   // taker being merged away
  const [mergeInto, setMergeInto] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    setErr('');
    try { setList(await api.listAdminCustomTakers()); }
    catch (e) { setErr(e?.message || '加载失败'); }
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  // Active, non-merged takers are the only valid merge targets.
  const mergeTargets = useMemo(
    () => (list || []).filter(t => t.active && !t.mergedInto && t.id !== mergeFrom?.id),
    [list, mergeFrom]
  );

  async function saveRename(id) {
    const name = editName.trim();
    if (!name) { setToast({ kind: 'error', msg: '姓名不能为空' }); return; }
    setBusy(true);
    try {
      await api.updateCustomTaker(id, { name });
      setToast({ kind: 'success', msg: '已重命名' });
      setEditId(null); setEditName('');
      await load();
    } catch (e) {
      setToast({ kind: 'error', msg: e?.message || '重命名失败' });
    } finally { setBusy(false); }
  }

  async function toggleActive(t) {
    setBusy(true);
    try {
      await api.updateCustomTaker(t.id, { active: !t.active });
      setToast({ kind: 'success', msg: t.active ? '已停用' : '已恢复' });
      await load();
    } catch (e) {
      setToast({ kind: 'error', msg: e?.message || '操作失败' });
    } finally { setBusy(false); }
  }

  async function doMerge() {
    if (!mergeFrom || !mergeInto) return;
    setBusy(true);
    try {
      await api.mergeCustomTaker(mergeFrom.id, mergeInto);
      const intoName = (list || []).find(t => t.id === mergeInto)?.name || '';
      setToast({ kind: 'success', msg: `已合并到「${intoName}」` });
      setMergeFrom(null); setMergeInto('');
      await load();
    } catch (e) {
      setToast({ kind: 'error', msg: e?.message || '合并失败' });
    } finally { setBusy(false); }
  }

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">取走人管理</h1>
          <div className="vava-page-meta">
            报废 · 店员取走 里手动输入的非员工姓名。重命名 / 停用 / 合并都不会改动库存记录。
          </div>
        </div>
      </div>

      {err && <div className="att-error inv-error" role="alert">{err}</div>}

      {list === null ? (
        <div className="vava-hint" style={{ padding: 16 }}>加载中…</div>
      ) : list.length === 0 ? (
        <div className="vava-empty" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 15, marginBottom: 4 }}>还没有自定义取走人</div>
          <div className="vava-hint">
            在 当日现货 → 报废 → 店员取走 选「其他（手动输入）」记录第一个名字后，这里会出现。
          </div>
        </div>
      ) : (
        <table className="vava-table">
          <thead>
            <tr>
              <th>姓名</th>
              <th style={{ textAlign: 'right' }}>使用次数</th>
              <th>最近使用</th>
              <th>状态</th>
              <th style={{ textAlign: 'right' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {list.map(t => (
              <tr key={t.id} style={{ opacity: t.active ? 1 : 0.55 }}>
                <td>
                  {editId === t.id ? (
                    <input className="vava-input" value={editName} maxLength={40} autoFocus
                           aria-label="新姓名"
                           onChange={(e) => setEditName(e.target.value)}
                           onKeyDown={(e) => { if (e.key === 'Enter') saveRename(t.id); if (e.key === 'Escape') setEditId(null); }} />
                  ) : t.name}
                </td>
                <td style={{ textAlign: 'right' }}>{t.usageCount}</td>
                <td className="vava-hint">{fmtWhen(t.lastUsedAt)}</td>
                <td>
                  {t.mergedInto ? <span className="vava-hint">已合并</span>
                    : t.active ? <span style={{ color: 'var(--vava-success, #2e7d32)' }}>启用</span>
                    : <span className="vava-hint">停用</span>}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {t.mergedInto ? null : editId === t.id ? (
                    <>
                      <button className="btn btn-primary btn-sm" disabled={busy}
                              onClick={() => saveRename(t.id)}>保存</button>
                      <button className="btn btn-secondary btn-sm" disabled={busy}
                              onClick={() => { setEditId(null); setEditName(''); }}>取消</button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-secondary btn-sm" disabled={busy}
                              onClick={() => { setEditId(t.id); setEditName(t.name); }}>重命名</button>
                      <button className="btn btn-secondary btn-sm" disabled={busy}
                              onClick={() => toggleActive(t)}>{t.active ? '停用' : '恢复'}</button>
                      {t.active && (
                        <button className="btn btn-secondary btn-sm" disabled={busy}
                                onClick={() => { setMergeFrom(t); setMergeInto(''); }}>合并</button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {mergeFrom && (
        <Modal
          title={`合并「${mergeFrom.name}」`}
          onClose={() => { setMergeFrom(null); setMergeInto(''); }}
          width={420}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => { setMergeFrom(null); setMergeInto(''); }}>取消</button>
              <button className="btn btn-danger" disabled={!mergeInto || busy} onClick={doMerge}>
                合并
              </button>
            </>
          }>
          <div style={{ fontSize: 14, lineHeight: 1.6 }}>
            <p>把「{mergeFrom.name}」并入另一个取走人。「{mergeFrom.name}」的历史记录会改为显示目标名字，
               并从下拉中移除。<strong>此操作不可撤销。</strong></p>
            <label className="vava-hint" style={{ display: 'block', marginTop: 8 }}>合并到</label>
            {mergeTargets.length === 0 ? (
              <div className="vava-hint">没有可作为目标的取走人。</div>
            ) : (
              <select className="vava-input" value={mergeInto} style={{ width: '100%', marginTop: 4 }}
                      onChange={(e) => setMergeInto(e.target.value)}>
                <option value="">选择目标…</option>
                {mergeTargets.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
          </div>
        </Modal>
      )}

      {toast && (
        <Toast message={toast.msg} kind={toast.kind} onDone={() => setToast(null)} />
      )}
    </>
  );
};
